import { server } from "../../server";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

function BusAttendanceStatistics() {
  const [attendanceDetails, setAttendanceDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const todayDate = new Date().toISOString().split("T")[0];
  const [markFilter, setMarkFilter] = useState("All");

  useEffect(() => {
    const fetchBusAttendanceStatus = async () => {
      try {
        // API 1: Summary data
        const summaryRes = await axios.post(
          `${server}/busAttendance_status_datewise_mongodb`,
          { date: todayDate },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
            },
          }
        );

        const summaryData = JSON.parse(
          summaryRes.data["body-json"]?.body || "[]"
        ); // parse directly to array

        // API 2: Marked attendance records
        const markedRes = await axios.post(
          `${server}/dailyAttendance_allRecords_dateWise_typeWise`,
          {
            date: todayDate,
            type: "bus",
          }
        );

        const markedData = markedRes.data["body-json"]?.body?.data || [];

        // Build sets for pickup and drop attendance
        const pickupMarked = new Set();
        const dropMarked = new Set();

        markedData.forEach((entry) => {
          const type = entry.attendance?.type;
          const busNo = entry.attendance?.busNo?.toString(); // normalize to string

          if (type === "busPickup") pickupMarked.add(busNo);
          if (type === "busDrop") dropMarked.add(busNo);
        });

        // Combine data
        const combinedData = summaryData.map((bus) => {
          const busNoStr = bus.BusNo?.toString(); // normalize to string
          return {
            ...bus,
            isPickupMarked: pickupMarked.has(busNoStr)
              ? "Marked"
              : "Not Marked",
            isDropMarked: dropMarked.has(busNoStr) ? "Marked" : "Not Marked",
          };
        });

        setAttendanceDetails(combinedData);
      } catch (error) {
        console.error("Error fetching bus attendance:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusAttendanceStatus();
  }, []);

  // Filter
  const expandedRecords = [];

  attendanceDetails.forEach((bus) => {
    const isPickupMarked = bus.isPickupMarked === "Marked";
    const isDropMarked = bus.isDropMarked === "Marked";

    switch (markFilter) {
      case "All":
        expandedRecords.push({ ...bus });
        break;
      case "Marked":
        if (isPickupMarked && isDropMarked) {
          expandedRecords.push({ ...bus });
        }
        break;
      case "Not Marked":
        if (!isPickupMarked && !isDropMarked) {
          expandedRecords.push({ ...bus });
        }
        break;
      case "Random":
        if (
          (isPickupMarked && !isDropMarked) ||
          (!isPickupMarked && isDropMarked)
        ) {
          expandedRecords.push({ ...bus });
        }
        break;
      default:
        break;
    }
  });

  const countStats = useMemo(() => {
    const stats = {
      all: attendanceDetails.length,
      marked: 0,
      notMarked: 0,
      random: 0,
    };

    attendanceDetails.forEach((bus) => {
      const isPickupMarked = bus.isPickupMarked === "Marked";
      const isDropMarked = bus.isDropMarked === "Marked";

      if (isPickupMarked && isDropMarked) stats.marked += 1;
      else if (!isPickupMarked && !isDropMarked) stats.notMarked += 1;
      else stats.random += 1;
    });

    return stats;
  }, [attendanceDetails]);

  // Search filter applied to expanded records
  const filteredRecords = expandedRecords.filter((record) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(record).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((attendance, index) => (
                          <tr>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Bus No</span> &mdash;{" "}
                              {attendance.BusNo} <br />
                              <hr className="w-25" />
                              <span class="text-dark">
                                Total PickUp Present
                              </span>{" "}
                              &mdash; {attendance.PickupPresent} <br />
                              <span class="text-dark">
                                Total PickUp Absent
                              </span>{" "}
                              &mdash; {attendance.PickupAbsent} <br />
                              <span class="text-dark">
                                Total Drop Present
                              </span>{" "}
                              &mdash; {attendance.DropPresent} <br />
                              <span class="text-dark">
                                Total Drop Absent
                              </span>{" "}
                              &mdash; {attendance.DropAbsent}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span
                                  className={`badge cursor-pointer px-3 rounded-3 ${
                                    attendance.isPickupMarked === "Marked"
                                      ? "bg-success-soft"
                                      : "bg-danger-soft"
                                  }`}
                                >
                                  Pickup &mdash;{" "}
                                  {attendance.isPickupMarked === "Marked"
                                    ? "Marked"
                                    : "Not Marked"}
                                </span>

                                <span
                                  className={`badge cursor-pointer px-3 rounded-3 ${
                                    attendance.isDropMarked === "Marked"
                                      ? "bg-success-soft"
                                      : "bg-danger-soft"
                                  }`}
                                >
                                  Drop &mdash;{" "}
                                  {attendance.isDropMarked === "Marked"
                                    ? "Marked"
                                    : "Not Marked"}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="bg-white rounded-pill">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Attendance</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Attendance Statistics
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div class="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                        <div class="avatar-group">
                          {[1, 2, 3, 4, 5].map((num) => (
                            <span className="avatar avatar-md" key={num}>
                              <img
                                alt={`avatar-${num}`}
                                src={`/assets/images/students/${num}.jpg`}
                                className="rounded-circle"
                              />
                            </span>
                          ))}
                        </div>
                        <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                          {attendanceDetails.length.toLocaleString("en-IN")}
                        </h3>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Attendance Statistics
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              1.8 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Class Wise
                                </span>
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Total Present
                                </span>
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Total Absent
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>

                      <div class="gap-1 d-flex flex-wrap mt-3">
                        <span
                          onClick={() => setMarkFilter("All")}
                          className={`btn btn-tag btn-sm ${
                            markFilter === "All"
                              ? "text-success-emphasis bg-success-subtle border border-success"
                              : ""
                          }`}
                        >
                          All
                        </span>
                        <span
                          onClick={() => setMarkFilter("Marked")}
                          className={`btn btn-tag btn-sm ${
                            markFilter === "Marked"
                              ? "text-success-emphasis bg-success-subtle border border-success"
                              : ""
                          }`}
                        >
                          Marked ({countStats.marked})
                        </span>
                        <span
                          onClick={() => setMarkFilter("Not Marked")}
                          className={`btn btn-tag btn-sm ${
                            markFilter === "Not Marked"
                              ? "text-danger-emphasis bg-danger-subtle border border-danger"
                              : ""
                          }`}
                        >
                          Not Marked ({countStats.notMarked})
                        </span>
                        <span
                          onClick={() => setMarkFilter("Random")}
                          className={`btn btn-tag btn-sm ${
                            markFilter === "Random"
                              ? "text-warning-emphasis bg-warning-subtle border border-warning"
                              : ""
                          }`}
                        >
                          Random ({countStats.random})
                        </span>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default BusAttendanceStatistics;
